import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * PhotoUploader
 * - value: array of storage paths (strings)
 * - onChange(newPaths)
 * - max: maximum number of photos
 * - disabled: read-only mode
 */
export default function PhotoUploader({ value = [], onChange, max = 10, disabled = false, testId = "photo-uploader", label }) {
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = Array.isArray(value) ? value : [];

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-pick same file
    if (!files.length) return;
    if (photos.length + files.length > max) {
      toast.error(`Máximo de ${max} fotos`);
      return;
    }
    setUploading(true);
    const next = [...photos];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, {
          headers: { "Content-Type": undefined },
        });
        next.push(data.path);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Falha ao enviar ${f.name}`);
      }
    }
    onChange?.(next);
    setUploading(false);
  };

  const remove = (idx) => {
    if (disabled) return;
    const next = photos.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div data-testid={testId}>
      {label && (
        <div className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 mb-2">{label}</div>
      )}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {photos.map((p, i) => (
          <div key={p + i} className="relative aspect-square border border-slate-200 rounded-sm overflow-hidden bg-slate-50 group" data-testid={`${testId}-thumb-${i}`}>
            <img
              src={`${API}/files/${p}`}
              alt={`Foto ${i + 1}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {!disabled && (
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute top-1 right-1 bg-white/95 hover:bg-red-50 border border-slate-200 text-slate-700 hover:text-red-600 rounded-sm w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testId}-remove-${i}`}
                title="Remover"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        {!disabled && photos.length < max && (
          <>
            <button
              type="button"
              onClick={() => cameraInput.current?.click()}
              disabled={uploading}
              className="aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center text-slate-500 hover:border-[#0052CC] hover:text-[#0052CC] transition-colors bg-white"
              data-testid={`${testId}-camera-button`}
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
              <span className="text-[10px] font-bold tracking-wider uppercase mt-1">Câmera</span>
            </button>
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="aspect-square border-2 border-dashed border-slate-300 rounded-sm flex flex-col items-center justify-center text-slate-500 hover:border-[#0052CC] hover:text-[#0052CC] transition-colors bg-white"
              data-testid={`${testId}-upload-button`}
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              <span className="text-[10px] font-bold tracking-wider uppercase mt-1">Upload</span>
            </button>
          </>
        )}
        {disabled && photos.length === 0 && (
          <div className="col-span-full text-xs text-slate-400 italic flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> Nenhuma foto registrada.
          </div>
        )}
      </div>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onPick}
        data-testid={`${testId}-camera-input`}
      />
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onPick}
        data-testid={`${testId}-file-input`}
      />

      <p className="text-[11px] text-slate-400 mt-2">
        {photos.length} / {max} fotos · JPG, PNG, WEBP, HEIC até 15MB cada.
      </p>
    </div>
  );
}
